/**
 * EPIC AUTH strings, English.
 *
 * Arabic is the source language; anything missing here falls back to it, so this
 * file is allowed to lag. It does not lag today.
 */
export const authEn: Record<string, string> = {
  // Mirrors the Arabic, which mirrors the landing page. See the note there.
  'auth.marketing.headline': 'From booking to payment',
  'auth.marketing.body':
    'One Arabic-first system for the clinic: appointments, records, lab, and billing.',
  'auth.marketing.claim1': "Every clinic's data is isolated",
  'auth.marketing.claim2': 'A finalised record cannot be edited',
  'auth.marketing.claim3': 'Medication checks do not depend on AI',
  'auth.marketing.footnote':
    'For clinics and medical centres — from one doctor to several branches',

  'auth.field.email': 'Email address',
  'auth.field.password': 'Password',

  'auth.login.title': 'Sign in',
  'auth.login.subtitle': 'Enter your account details to continue to your clinic workspace.',
  'auth.login.forgot': 'Forgot your password?',
  'auth.login.submit': 'Sign in',
  'auth.login.busy': 'Checking your details…',
  'auth.login.needsBoth': 'Enter both your email address and password',
  'auth.login.noClinic': 'Clinic not registered yet?',
  'auth.login.registerClinic': 'Register a new clinic',
  'auth.login.error': 'That email address or password is not correct.',
  'auth.login.rateLimited': 'Too many attempts. Wait a minute and try again.',

  'auth.mfa.title': 'Two-step verification',
  'auth.mfa.subtitle': 'Enter the 6-digit code from your authenticator app.',
  'auth.mfa.codeLabel': 'Verification code',
  'auth.mfa.submit': 'Verify and sign in',
  'auth.mfa.needsCode': 'Enter the full code',
  'auth.mfa.useRecovery': 'Use a recovery code',
  'auth.mfa.useApp': 'Back to the app code',
  'auth.mfa.recoveryLabel': 'Recovery code',
  'auth.mfa.recoveryHint':
    'One of the codes you saved when you turned on two-step verification. Each works once.',
  'auth.mfa.attemptsRemaining': '{count} attempts left',
  'auth.mfa.wrongCode': 'That code is not correct. Check your device clock and try again.',
  'auth.mfa.exhausted': 'No attempts left. Sign in again.',
  'auth.mfa.back': 'Back to sign in',

  'auth.mfa.enrolTitle': 'Two-step verification required',
  'auth.mfa.enrolSubtitle':
    'Your organisation requires two-step verification for your role. Scan the setup code in your authenticator app, then enter the code it shows.',
  'auth.mfa.enrolSubmit': 'Turn on and sign in',

  'auth.recovery.title': 'Recovery codes',
  'auth.recovery.subtitle':
    'Keep these somewhere safe. Each code works once, if you lose your authenticator device.',
  'auth.recovery.warning':
    'These will not be shown again. Only their hashes are stored, and neither support nor a platform administrator can retrieve them.',
  'auth.recovery.acknowledge':
    'I have saved these codes somewhere safe and understand they will not be shown again.',
  'auth.recovery.acknowledgeFirst': 'Confirm you have saved the codes first',
  'auth.recovery.continue': 'Continue to the app',

  'auth.forgot.title': 'Reset your password',
  'auth.forgot.subtitle':
    'Enter your email address and we will send a reset link, valid for 30 minutes.',
  'auth.forgot.submit': 'Send the link',
  'auth.forgot.needsEmail': 'Enter your email address',
  'auth.forgot.noConfirmation':
    'If that address is registered, the link is on its way. We do not confirm whether an account exists, for security reasons.',
  'auth.forgot.backToLogin': 'Back to sign in',
  'auth.forgot.sentTitle': 'Check your email',
  'auth.forgot.sentBody':
    'If that address is registered, a reset link will arrive within a few minutes.',
  'auth.forgot.rateLimited': 'Too many requests. Wait a few minutes and try again.',
  'auth.forgot.error': 'The request could not be sent. Try again shortly.',

  'auth.login.verifyRequired':
    'Your email address has not been confirmed yet. Open the link we sent you, then sign in again.',
  'auth.login.verifyResend': 'Send the confirmation link again',
  'auth.login.verifyResent':
    'If that address is registered and needs confirming, a new link is on its way. Check your email, then sign in again.',
  'auth.verify.title': 'Confirm your email',
  'auth.verify.subtitle': 'Press the button to confirm this address is yours.',
  'auth.verify.action': 'Confirm email',
  'auth.verify.note':
    'The link works once, and nothing is confirmed until you press the button — so a link scanner in your mailbox cannot use it up first.',
  'auth.verify.doneTitle': 'Your email is confirmed',
  'auth.verify.doneBody': 'You can sign in and continue to the clinic workspace.',
  'auth.verify.toLogin': 'Go to sign in',
  'auth.verify.failedTitle': 'That link is no longer valid',
  'auth.verify.failedBody':
    'A confirmation link lasts 24 hours and can be used once. Enter your email to send a new one.',
  'auth.verify.noToken':
    'This page opens from the link in your email. Enter your address to send a new one.',
  'auth.verify.emailLabel': 'Email address',
  'auth.verify.resendAction': 'Send a new link',
  'auth.verify.resendMaybe':
    'If that address is registered and needs confirming, a new link is on its way. We do not confirm whether an account exists.',
  'auth.verify.backToLogin': 'Back to sign in',
  'auth.reset.title': 'Set a new password',
  'auth.reset.subtitle': 'Choose a strong password. All your current sessions end when you save.',
  'auth.reset.newPassword': 'New password',
  'auth.reset.confirmPassword': 'Confirm password',
  'auth.reset.mismatch': 'The two passwords do not match.',
  'auth.reset.rulesLabel': 'Password requirements',
  'auth.reset.ruleLength': 'At least {min} characters',
  'auth.reset.ruleLetter': 'At least one letter',
  'auth.reset.ruleDigit': 'At least one digit',
  'auth.reset.policyNotMet': 'The password does not meet the requirements',
  'auth.reset.submit': 'Save password',
  'auth.reset.invalidToken': 'That link has expired or was already used. Request a new one.',
  'auth.reset.error': 'The password could not be saved. Try again.',
  'auth.reset.doneTitle': 'Password updated',
  'auth.reset.doneBody': 'You can now sign in with your new password.',
  'auth.reset.sessionsRevoked': 'Sessions on other devices were ended to protect your account.',
  'auth.reset.goToLogin': 'Sign in',
  'auth.reset.noTokenTitle': 'Invalid link',
  'auth.reset.noTokenBody': 'This link carries no reset token. Request a new one.',
  'auth.reset.requestNew': 'Request a new link',

  'auth.idle.title': 'Your session is about to end',
  'auth.idle.body':
    'We have not seen any activity for a while. The session will end automatically to protect patient data on this device.',
  'auth.idle.remaining': 'Time remaining',
  'auth.idle.stay': 'Keep working',
  'auth.idle.extending': 'Extending the session…',
  'auth.idle.signOutNow': 'Sign out now',

  'auth.denied.missing_permission': 'You do not have permission for this action.',
  'auth.denied.branch_not_granted': 'This record belongs to a branch you are not assigned to.',
  'auth.denied.not_own_record': 'Your access is limited to your own records.',
  'auth.denied.staff_suspended': 'Your account has been suspended. Contact your administrator.',
  'auth.denied.staff_inactive': 'Your account is not active.',
  'auth.denied.not_a_member': 'You are not a member of this organisation.',
  'auth.denied.tenant_suspended':
    'This organisation is suspended. Billing and account pages remain available.',
  'auth.denied.reauth_required': 'This is a sensitive action and needs your password again.',
  'auth.denied.idle_timeout': 'Your session ended through inactivity. Sign in again.',
};
